import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { SEOHead } from '../components/SEOHead';
import { TechSection } from '../components/TechSection';
import { StatsSection } from '../components/StatsSection';
import { HowItWorks } from '../components/HowItWorks';
import { Features } from '../components/Features';
import { ProductHero } from '../components/ProductHero';
import { AlgorithmProcess } from '../components/AlgorithmProcess';
import { CustomWorkflow } from '../components/CustomWorkflow';
import { FAQ } from '../components/FAQ';
import { PricingSection } from '../components/PricingSection';
import GoogleStartupsLogo from '../assets/logos/google-for-startups.svg';

const Home = () => {
  const { t } = useTranslation();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const dynamicTextOptions = t('hero.dynamicText.options', { returnObjects: true }) as string[];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((current) => 
        current === dynamicTextOptions.length - 1 ? 0 : current + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [dynamicTextOptions.length]);

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        path="/"
      />

      <article>
        <section aria-label="Hero" className="section min-h-screen flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold space-y-4">
                <div>
                  {t('hero.title')}
                </div>
                <div className="relative h-[1.2em] overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentTextIndex}
                      className="absolute inset-0 text-gradient"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                    >
                      {dynamicTextOptions[currentTextIndex]}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </h1>

              <p className="text-xl text-gray-600 dark:text-gray-300">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-wrap gap-4">
                <motion.a
                  href="https://calendly.com/alex-craft-with-ai/demo-de-craftseo?month=2025-01"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 rounded-full premium-gradient text-white font-medium"
                >
                  {t('hero.cta')}
                </motion.a>
              </div>

              <div className="pt-8 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon
                      key={i}
                      icon={faStarSolid}
                      className="text-yellow-400 w-5 h-5"
                    />
                  ))}
                  <span className="ml-2 font-medium">{t('hero.trust.rating')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('hero.trust.reviews')}
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={GoogleStartupsLogo}
                    alt="Google for Startups"
                    className="h-10 w-auto object-contain"
                  />
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('hero.trust.googlePartner')}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="https://ziyrlqyuwnxpkliapfnt.supabase.co/storage/v1/object/public/images/WiseRank/wiserank.png"
                  alt="WiseRank.ai Platform"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        <TechSection />
        <StatsSection />
        <ProductHero />
        <AlgorithmProcess />
        <HowItWorks />
        <Features />
        <PricingSection />
        <CustomWorkflow />
        <FAQ />
      </article>
    </>
  );
};

export default Home;
export { Home };